import { createAuthClient } from "better-auth/react";
import { usernameClient } from "better-auth/client/plugins";
import { ConvexHttpClient } from "convex/browser";
import type {
  FunctionArgs,
  FunctionReference,
  FunctionReturnType,
} from "convex/server";
import {
  convexClient,
  crossDomainClient,
} from "@convex-dev/better-auth/client/plugins";

export const authClient = createAuthClient({
  baseURL: import.meta.env.VITE_CONVEX_SITE_URL,
  plugins: [usernameClient(), convexClient(), crossDomainClient()],
});

export const { signIn, signUp, signOut, useSession } = authClient;

const convexUrl = import.meta.env.VITE_CONVEX_URL as string;
const authSiteUrl = import.meta.env.VITE_CONVEX_SITE_URL as string;
const delay = (ms: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, ms));

const getCrossDomainAuthCookie = () => {
  const cookie = (authClient as typeof authClient & { getCookie?: () => string }).getCookie?.();
  return cookie && cookie.trim().length > 0 ? cookie : null;
};

const fetchConvexAuthToken = async () => {
  try {
    const authCookie = getCrossDomainAuthCookie();
    const response = await fetch(
      `${authSiteUrl.replace(/\/+$/, "")}/api/auth/convex/token`,
      {
        credentials: authCookie ? "omit" : "include",
        headers: authCookie
          ? {
              "Better-Auth-Cookie": authCookie,
            }
          : undefined,
      }
    );

    if (!response.ok) {
      return null;
    }

    const data = await response.json().catch(() => null);
    return typeof data?.token === "string" && data.token.length > 0
      ? data.token
      : null;
  } catch {
    return null;
  }
};

const hasConvexToken = async () => Boolean(await fetchConvexAuthToken());

export const runAuthenticatedMutation = async <
  Mutation extends FunctionReference<"mutation">,
>(
  mutation: Mutation,
  args: FunctionArgs<Mutation>,
  {
    timeoutMs = 30000,
    intervalMs = 250,
  }: {
    timeoutMs?: number;
    intervalMs?: number;
  } = {}
): Promise<FunctionReturnType<Mutation>> => {
  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    const token = await fetchConvexAuthToken();
    if (token) {
      const client = new ConvexHttpClient(convexUrl);
      client.setAuth(token);
      return await client.mutation(mutation, args);
    }

    await delay(intervalMs);
  }

  throw new Error(
    `Could not acquire a Convex auth token for account setup within ${timeoutMs}ms`
  );
};

export const waitForConvexAuthBridge = async ({
  timeoutMs = 30000,
  intervalMs = 250,
}: {
  timeoutMs?: number;
  intervalMs?: number;
} = {}) => {
  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    // The Convex token endpoint is the real gate for authenticated mutations.
    // Better Auth's session helper can lag behind cross-domain cookie updates,
    // which would otherwise strand users on signup/auth-recovery despite a live bridge.
    if (await hasConvexToken()) {
      return true;
    }

    await authClient.getSession().catch(() => null);

    await delay(intervalMs);
  }

  return false;
};
